import { LdJsonOrganization, LdJsonSearchAction } from 'components/LdJson';
import { AdUnitContext } from 'googlead/components/AdUnitContext';
import { TopAd } from 'googlead/components/TopAd';
import { useOTSTranslation } from 'i18n/useOTSTranslation';
import { HeadMeta } from 'components/common/HeadMeta';
import { HeaderNav } from 'components/common/HeaderNav';
import { Layout } from 'components/resort/Layout/Layout';
import { Main } from 'components/resort/Layout/Main';
import { BottomMountains } from 'components/blocks/BottomMountains';
import { Footer } from 'components/blocks/Footer';
import { RouteAndAnalyticsProps } from 'page-components/pathTypes';
import React, { useCallback, useState } from 'react';
import { HomeHero } from 'components/home/HomeHero';
import {
  fetchBackgroundImages,
  fetchFeaturedResort,
  fetchFirstHandReports,
  fetchLastNews,
  fetchPopularSearch,
  fetchResortTopRating,
  fetchSnowWebcams,
  fetchMostReportedSnowfall,
} from 'api/home/static';
import {
  fetchRegionResortListGuide,
} from 'api/region/static';
import {
  fetchResortDescriptionTabs,
} from 'api/resort/static';
import {
  IBackgroundImages,
  IFeaturedResort,
  INews,
  IPopularSearch,
  IResortTopRated,
  ISnowWebcam,
  ResortTopRated,
  IFirstHandReportsResponse,
  IMostReportedSnowfallResponse,
} from 'api/home/types';
import { BestResorts } from 'components/home/BestResorts';
import { NewsList } from 'components/home/NewsList';
import { Routes } from 'routes';
import { useRouter } from 'next/router';
import { getPortalOptions } from 'util/getPortalConfig';
import { isMainLocale } from 'util/i18nFlags';
import IntegrityError from 'page-components/400';
import { wrapServerSideProps, wrapStaticProps } from 'util/wrapProps';
import { propsWithI18NSSR } from 'util/propsWithI18n';
import GlobalTemplateWeSkiWidget from 'components/common/PlanYourTrip/WeSki/GlobalTemplateWeSkiWidget';
import { HeaderNavCurLink } from 'components/common/HeaderNav/MobileSummerHeader';
import CamsAndSnowfallSection from 'components/home/CamsAndSnowfallSection';
import { NewGuideSlider } from 'components/resort/NewGuideSlider';
import { FeaturedResort } from 'components/home/FeaturedResort';
import { IResortPathInfo, IDescriptionsResort } from 'api/resort/types';
import { getLocale } from 'i18n/locale';
import localeEnumToRegionUuid from 'components/home/MostReportedSnowfall/localeEnumToRegionUuid';
import { ELocale, EULocales } from 'i18n/localeEnum';
import { fetchRegionSlugs } from 'util/fetchStaticPageRegion';

const EUROPE_UUID = 476;

export type HomePageProps = {
  popularSearch: IPopularSearch,
  backgroundImage: IBackgroundImages,
  snowWebcams: Array<ISnowWebcam>,
  resortTopRatingOverall: Array<IResortTopRated>,
  lastNews: Array<INews> | null,
  featuredResort: IFeaturedResort | null,
  featuredResortRegionResorts?: Array<IResortPathInfo>,
  featuredResortDescriptionTabs?: Array<IDescriptionsResort>,
  firstHandReports?: IFirstHandReportsResponse,
  regionResorts?: Array<IResortPathInfo>,
  type?: 'global' | undefined,
  mostReportedSnowfall?: IMostReportedSnowfallResponse | null,
  webcamsLinkRegionSlug?:string;
} & RouteAndAnalyticsProps;

export const DEBUG_TAG = 'home-page';
export const TEMPLATE_ID = '1.0';
export const TEMPLATE_NAME = 'Home Page';

export default function HomePage({
  popularSearch,
  backgroundImage,
  snowWebcams,
  resortTopRatingOverall,
  mostReportedSnowfall,
  firstHandReports,
  regionResorts,
  lastNews,
  featuredResort,
  featuredResortRegionResorts,
  featuredResortDescriptionTabs,
  type,
  webcamsLinkRegionSlug,
}: HomePageProps) {
  const { hasNews } = getPortalOptions();

  const { t } = useOTSTranslation('common');
  const { t: seoT } = useOTSTranslation('seo');

  if (!popularSearch || !backgroundImage) {
    return <IntegrityError />;
  }

  const [searchQuery, setSearchQuery] = useState<string | string[] | undefined>();
  const router = useRouter();

  const clearSearchQuery = useCallback(async () => {
    if (global?.location?.search) {
      await router.replace(global?.location?.pathname);
    }

    setSearchQuery(undefined);
  }, [searchQuery]);

  const { query } = router;

  if (!searchQuery && query.q) {
    setSearchQuery(query.q);
  }

  return (
    <div style={{ overflowX: 'hidden' }}>
      <AdUnitContext
        adType="home"
        adTKey="other home"
      >
        <HeadMeta
          title={seoT('Other.home.title')}
          description={seoT('Other.home.description')}
          image={backgroundImage.image}
        />
        <HeaderNav current={HeaderNavCurLink.MAIN} />
        <TopAd id="home" />
        <HomeHero
          backgroundImage={backgroundImage}
          popularSearch={popularSearch}
          searchQuery={searchQuery}
          clearSearchQuery={clearSearchQuery}
        />
        <div className="container-xl content-container home-page-container">
          <Layout className="layout-container-noback">
            <Main>
              <h1 className="home-page-h1-title">{t('homePage.h1Title')}</h1>
              <CamsAndSnowfallSection
                snowWebcamsProps={{
                  headerScope: 'h2',
                  isFresh: true,
                  showSnowReport: true,
                  snowWebcams,
                  imageLink: Routes.resortSnowReport,
                  titleLink: Routes.resortSnowReport,
                  header: t('common.featuredWebcams'),
                }}
                currentRegionSlug={webcamsLinkRegionSlug}
                mostReportedSnowfallItems={mostReportedSnowfall?.data ?? null}
              />
              {lastNews && hasNews
              && (
                <NewsList
                  areNewsFresh
                  title={t('homePage.latestNews')}
                  news={lastNews}
                />
              )}

              <GlobalTemplateWeSkiWidget />
              {firstHandReports && firstHandReports.reportsList && firstHandReports.reportsList.length > 0 && (
              <NewGuideSlider
                title={t('nav.tab.fhr')}
                slides={[]}
                reports={firstHandReports.reportsList}
                regionResorts={regionResorts || []}
              />
              )}
              {featuredResort
              && (
              <FeaturedResort
                resort={featuredResort}
                regionResorts={featuredResortRegionResorts}
                resortDescriptionTabs={featuredResortDescriptionTabs}
              />
              )}
              {
              isMainLocale()
              && resortTopRatingOverall
              && (
                <BestResorts
                  pageType={type}
                  resortTopRatingOverall={resortTopRatingOverall}
                  isUsedInHomePage
                />
              )
            }
            </Main>
            <LdJsonSearchAction />
            <LdJsonOrganization />
          </Layout>
        </div>
        <BottomMountains />
        <Footer />
      </AdUnitContext>
    </div>
  );
}

export const fetchProps = async (): Promise<HomePageProps> => {
  const locale = getLocale();
  const currentRegionUuid = locale ? localeEnumToRegionUuid[locale as ELocale] : undefined;
  const webcamsLinkRegionUuid = EULocales.has(locale as ELocale) ? EUROPE_UUID : localeEnumToRegionUuid[locale as ELocale];
  const regionGroups = await fetchRegionSlugs(DEBUG_TAG);
  const regionNameById = regionGroups.reduce<Record<string, string>>((acc, region) => {
    acc[region.uuid] = region.title;
    return acc;
  }, {});
  const currentRegion = regionGroups.find((group) => group.uuid === currentRegionUuid?.toString());
  const webcamsRegion = regionGroups.find((group) => group.uuid === webcamsLinkRegionUuid?.toString());
  const currentRegionGroupUuid = currentRegion ? currentRegion.group : null;

  const popularSearch = await fetchPopularSearch();
  const backgroundImage = await fetchBackgroundImages();
  const snowWebcams = await fetchSnowWebcams(6);
  const snowWebcamsWithRegion = snowWebcams.map((item) => ({
    ...item,
    regionName: regionNameById[item.resort.region_id] ?? item.resort.region_en,
  }));
  const resortTopRatingOverall = await fetchResortTopRating(ResortTopRated.overall);
  const { hasNews } = getPortalOptions();
  const lastNews = hasNews ? (await fetchLastNews()) : null;
  const featuredResort = await fetchFeaturedResort();
  const firstHandReports = await fetchFirstHandReports(1);

  let regionResorts;

  if (currentRegion?.uuid !== currentRegion?.group) {
    const regionResortsByUuid = currentRegionUuid ? await fetchRegionResortListGuide(currentRegionUuid.toString()) : undefined;
    const regionResortsByGroup = currentRegionGroupUuid ? await fetchRegionResortListGuide(currentRegionGroupUuid.toString()) : undefined;
    regionResorts = [...(regionResortsByUuid || []), ...(regionResortsByGroup || [])];
  } else {
    const regionResortsByUuid = currentRegionUuid ? await fetchRegionResortListGuide(currentRegionUuid.toString()) : undefined;
    regionResorts = regionResortsByUuid;
  }

  const featuredResortRegionResorts = featuredResort?.region?.uuid
    ? await fetchRegionResortListGuide(featuredResort.region.uuid.toString())
    : undefined;

  const featuredResortDescriptionTabs = featuredResort?.uuid
    ? await fetchResortDescriptionTabs(featuredResort.uuid.toString())
    : undefined;

  const mostReportedSnowfall = currentRegionUuid
    ? await fetchMostReportedSnowfall(currentRegionUuid.toString())
    : null;

  return {
    type: 'global',
    route: 'index',
    popularSearch,
    backgroundImage,
    snowWebcams: snowWebcamsWithRegion,
    resortTopRatingOverall,
    lastNews,
    featuredResort,
    featuredResortRegionResorts,
    featuredResortDescriptionTabs,
    firstHandReports,
    regionResorts,
    mostReportedSnowfall,
    templateName: TEMPLATE_NAME,
    templateId: TEMPLATE_ID,
    prefetch: false,
    webcamsLinkRegionSlug: webcamsRegion?.slug,
  };
};

// This function gets called at ISR build time

export const getStaticPropsDeprecated = wrapStaticProps(fetchProps);

export const getServerSideProps = propsWithI18NSSR(wrapServerSideProps(fetchProps));
